import { useMutation } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputText from "@repo/components/src/forms/text";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { VStack } from "@repo/components/src/ui/vstack";
import { CreateBrandPostulationTagDocument } from "@repo/graphql/documents/postulationTag/mutation.generated";
import { BrandPostulationTagsDocument } from "@repo/graphql/documents/postulationTag/query.generated";
import { useForm } from "react-hook-form";

import { Text } from "@repo/components/src/ui/text";
import ColorPicker from "./colorPicker";
import { COLOR_PALETTE, TagFormSchema, tagFormSchema } from "./tagFormSchema";

interface CreateTagFormProps {
  brandId: string;
  onCreated: (tagId: string) => void;
  onCancel: () => void;
}

export default function CreateTagForm({
  brandId,
  onCreated,
  onCancel,
}: CreateTagFormProps) {
  const form = useForm<TagFormSchema>({
    resolver: yupResolver(tagFormSchema),
    defaultValues: { name: "", color: COLOR_PALETTE[0].hex },
  });

  const [createTag, { loading }] = useMutation(
    CreateBrandPostulationTagDocument,
    {
      refetchQueries: [BrandPostulationTagsDocument],
      onCompleted: (data) => {
        const errors = data.postulationTag.create.userErrors;
        if (errors && errors.length > 0) {
          MontuToastProvider.show({
            placement: "bottom",
            duration: 3000,
            render: ({ id }) => (
              <Toast nativeID={`toast-${id}`} action="error" variant="solid">
                <ToastDescription>{errors[0].message}</ToastDescription>
              </Toast>
            ),
          });
          return;
        }
        const createdTag = data.postulationTag.create.postulationTag;
        if (createdTag) {
          const tag = createdTag;
          onCreated(tag.id);
        }
        form.reset({ name: "", color: COLOR_PALETTE[0].hex });
      },
      onError: () => {
        MontuToastProvider.show({
          placement: "bottom",
          duration: 3000,
          render: ({ id }) => (
            <Toast nativeID={`toast-${id}`} action="error" variant="solid">
              <ToastDescription>Error al crear tag</ToastDescription>
            </Toast>
          ),
        });
      },
    },
  );

  const onSubmit = (data: TagFormSchema) => {
    createTag({
      variables: { brandId, name: data.name, color: data.color },
    });
  };

  return (
    <VStack space="md" className="p-2 bg-background-50 rounded-lg">
      <Text>Crear tag</Text>
      <FormInputText
        control={form.control}
        name="name"
        label={undefined}
        placeholder="Nombre del tag"
        required
        size="sm"
        keyboardType="default"
        textContentType="none"
        autoCapitalize="none"
      />
      <ColorPicker control={form.control} name="color" required size="sm" />
      <HStack space="xs">
        <LoadingButton
          size="xs"
          action="brand"
          loading={loading}
          onPress={() => form.handleSubmit(onSubmit)()}
          className="flex-1"
        >
          Crear
        </LoadingButton>
        <Button
          size="xs"
          variant="outline"
          action="muted"
          onPress={onCancel}
          className="flex-1"
        >
          <ButtonText>Cancelar</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
